import { useEffect, useRef, useState } from "react";

export const useReducedMotion = () => {
  const [reduce, setReduce] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const on = (e) => setReduce(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
};

// Adds .in once the element enters the viewport (fade-up on scroll).
export const useInView = (rootMargin = "0px 0px -12% 0px") => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin, threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);
  return [ref, inView];
};

export const Reveal = ({ as: Tag = "div", className = "", children, ...rest }) => {
  const [ref, inView] = useInView();
  return (
    <Tag ref={ref} className={`sj-reveal ${inView ? "in" : ""} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

// Hand-painted ensō, revealed as one clockwise brush stroke through a conic-gradient mask. Drifts ~10% with scroll.
export const Enso = ({ reduce, onPainted }) => {
  const ref = useRef(null);
  const [done, setDone] = useState(reduce);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (reduce) { setDone(true); onPainted?.(); return undefined; }
    const img = el.querySelector("img");
    const start = performance.now();
    const DUR = 1600;
    let raf;
    const ease = (t) => 1 - Math.pow(1 - t, 2.2);
    const step = (now) => {
      const t = Math.min(1, (now - start) / DUR);
      img.style.setProperty("--sweep", `${ease(t) * 360}deg`);
      if (t < 1) raf = requestAnimationFrame(step);
      else { setDone(true); onPainted?.(); }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [reduce, onPainted]);

  useEffect(() => {
    if (reduce) return undefined;
    const el = ref.current;
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { if (el) el.style.transform = `translateY(${window.scrollY * 0.06}px)`; });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [reduce]);

  return (
    <div ref={ref} className={`sj-enso ${done ? "is-done" : ""}`} data-testid="sojourns-enso">
      <img
        src="/sojourn-media/enso-copper-1100.webp"
        alt="The Savi ensō, a single hand-painted brush circle in copper"
        width="1072"
        height="1100"
        fetchpriority="high"
        decoding="async"
      />
    </div>
  );
};

// Slow silent film behind a section: poster first, lazy video, paused off screen, still if data saver or reduced motion.
export const Film = ({ name, reduce }) => {
  const ref = useRef(null);
  const [armed, setArmed] = useState(false);
  const saveData = typeof navigator !== "undefined" && navigator.connection?.saveData === true;
  const still = reduce || saveData;

  useEffect(() => {
    if (still) return undefined;
    const wrap = ref.current;
    if (!wrap) return undefined;
    const io = new IntersectionObserver((entries) => {
      const video = wrap.querySelector("video");
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setArmed(true);
          video?.play?.().catch(() => {});
        } else {
          video?.pause?.();
        }
      });
    }, { rootMargin: "200px 0px" });
    io.observe(wrap);
    return () => io.disconnect();
  }, [still]);

  // Sources are set as a plain src (not <source> children) so the browser always picks them up, then load() kicks it off.
  useEffect(() => {
    if (!armed || still) return;
    const video = ref.current?.querySelector("video");
    if (!video) return;
    video.load();
    video.play?.().catch(() => {});
  }, [armed, still]);

  const poster = `/sojourn-media/film/${name}.jpg`;
  const ext = typeof document !== "undefined" && document.createElement("video").canPlayType("video/webm") ? "webm" : "mp4";
  // Phones get the 640px encode (about a quarter of the bytes); the film is a texture, not a feature.
  const small = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  const file = small ? `/sojourn-media/film/${name}-m.mp4` : `/sojourn-media/film/${name}.${ext}`;
  return (
    <div ref={ref} className="sj-film" aria-hidden="true" data-testid={`sojourns-film-${name}`}>
      {still ? (
        <img src={poster} alt="" loading="lazy" decoding="async" />
      ) : (
        <video
          muted
          loop
          playsInline
          autoPlay
          preload={armed ? "auto" : "none"}
          poster={poster}
          disablePictureInPicture
          src={armed ? file : undefined}
        />
      )}
    </div>
  );
};

// The destination, redacted. Hover or tap shows the line for a few seconds, then it closes again.
export const Redaction = ({ reduce, text }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const show = () => {
    setOpen(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 3200);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  if (text) return <span className="sj-unredacted" data-testid="sojourns-destination">{text}</span>;
  return (
    <button
      type="button"
      className={`sj-redact ${open ? "open" : ""}`}
      onClick={show}
      onMouseEnter={show}
      onFocus={show}
      aria-label={open ? "Destination: we'll tell you when you're in." : "Destination: redacted. Press to find out more."}
      data-testid="sojourns-destination"
    >
      <span className="reveal" aria-hidden={!open} role="status">we'll tell you when you're in.</span>
    </button>
  );
};

// Page chrome: near-black behind overscroll while a Sojourns page is mounted, no cream from the main site.
export const useSojournsChrome = () => {
  useEffect(() => {
    const html = document.documentElement;
    const prev = { bg: html.style.backgroundColor, body: document.body.style.backgroundColor };
    html.style.backgroundColor = "#12100E";
    document.body.style.backgroundColor = "#12100E";
    return () => { html.style.backgroundColor = prev.bg; document.body.style.backgroundColor = prev.body; };
  }, []);
};

// Days until the reveal date (local midnight). null hides the line.
export const useCountdown = (revealDate) => {
  const [days, setDays] = useState(null);
  useEffect(() => {
    if (!revealDate) { setDays(null); return undefined; }
    const compute = () => {
      const target = new Date(`${revealDate}T00:00:00`);
      if (Number.isNaN(target.getTime())) { setDays(null); return; }
      const now = new Date();
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setDays(Math.round((target - startToday) / 86400000));
    };
    compute();
    const id = setInterval(compute, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [revealDate]);
  return days;
};

// A thin moon that fills from new to full over the last lunar month before the gate opens.
export const Moon = ({ days, size = 30 }) => {
  const r = 14;
  const f = days === null ? 0 : Math.max(0, Math.min(1, 1 - days / 29.5));
  const rx = Math.abs(1 - 2 * f) * r;
  const lit = f <= 0.005 ? ""
    : f >= 0.999
      ? `M 0 ${-r} A ${r} ${r} 0 1 1 0 ${r} A ${r} ${r} 0 1 1 0 ${-r} Z`
      : `M 0 ${-r} A ${r} ${r} 0 0 1 0 ${r} A ${rx} ${r} 0 0 ${f < 0.5 ? 0 : 1} 0 ${-r} Z`;
  // Always a plain child (no conditional), so the dev visual-edit plugin can't wrap it in a span inside the SVG.
  return (
    <svg className="sj-moon" width={size} height={size} viewBox="-16 -16 32 32" role="img" aria-label={`Moon ${Math.round(f * 100)} per cent lit, full when the gate opens`} focusable="false" data-testid="sojourns-moon" data-lit={Math.round(f * 100)}>
      <circle r={r} fill="none" stroke="#EDE6DB" strokeOpacity="0.45" strokeWidth="0.8" />
      <path d={lit} fill="#EDE6DB" />
    </svg>
  );
};

// Cabin plan: twelve seats, two rows of six with an aisle. Held seats are filled.
const WORDS = ["none", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
export const SeatPlan = ({ held = [], founder = [] }) => {
  const set = new Set(held);
  const fnd = new Map(founder.map((f) => [f.seat, f.status]));
  const rows = [[1, 2, 3, 4, 5, 6], [7, 8, 9, 10, 11, 12]];
  const label = `Twelve seats, ${WORDS[set.size]} held${fnd.size ? `, ${WORDS[fnd.size]} founder` : ""}.`;
  return (
    <div className="sj-seats" role="img" aria-label={label} data-testid="sojourns-seats">
      {rows.map((row, r) => (
        <div className="sj-seat-row" key={r}>
          {row.map((n, i) => {
            const f = fnd.get(n);
            const cls = f === "paid" ? "founder" : f === "held" ? "founder-held" : set.has(n) ? "held" : "";
            return <span key={n} className={`sj-seat ${cls} ${i === 3 ? "aisle" : ""}`} data-testid={`sojourns-seat-${n}`} data-held={set.has(n) || !!f ? "true" : "false"} data-founder={f || undefined} />;
          })}
        </div>
      ))}
    </div>
  );
};

const BARS = [3, 1, 2, 1, 4, 1, 1, 3, 2, 1, 1, 2, 4, 1, 3, 1, 1, 2, 1, 3, 2, 1, 4, 1, 2, 2, 1, 3, 1, 1, 2, 4, 1, 1, 3, 2, 1, 2, 1, 1, 3, 1];
// One path, so nothing wraps the bars (the dev visual-edit plugin wraps arrays of children, which SVG can't render).
const BAR_PATH = (() => {
  let x = 0;
  const parts = [];
  BARS.forEach((w, i) => { if (i % 2 === 0) parts.push(`M${x} 0h${w}v44h-${w}z`); x += w + 1; });
  return { d: parts.join(""), width: x };
})();
export const Barcode = () => (
  <svg viewBox={`0 0 ${BAR_PATH.width} 44`} role="img" aria-label="Barcode" focusable="false">
    <path d={BAR_PATH.d} fill="#EDE6DB" />
  </svg>
);
